import { useEffect, useState, type FC } from 'react';

import {
  AutoTruncateText,
  checkEmpty,
  Chip,
  Drawer,
  getStoreValue,
  HighlightText,
  Icon,
  NoDataContent,
  PopUpModal,
  Search,
  Table,
  toast,
  Typography,
} from 'pixel-react';
import { loadOngoingExecutions } from 'service/hostExecutionDrawerSlice';

import MachineOrUserDetailsDrawer from '@/components/ExecutionDetails/MachineOrUserDetailsDrawer';
import type {
  ExecutionDetailsProps,
  MachineDetails,
  MachineTableData,
  UserDetails,
  UserTableData,
} from '@/components/ExecutionDetails/types';
import { DynamicObj } from '@/lib/utils/commonTypes';
import {
  useGetUserDetailsQuery,
  useLazyGetExecutionDetailsQuery,
  useTerminateExecutionMutation,
} from '@/store/executionDetailsDrawerApi';
import { ExecutionResponseObjType } from '@/store/executionDetailsDrawerApi/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import styles from './ExecutionDetailsDrawer.module.scss';
import { LicenseType } from '../chatbot/types';

const ExecutionDetails: FC<ExecutionDetailsProps> = ({
  onClose,
  rowData,
  scriptType,
  terminated,
  onGoingFetchData,
}) => {
  const [type, setType] = useState<string>('');
  const [details, setData] = useState<MachineDetails | UserDetails>({
    os: '',
    browser: '',
    device: '',
    owner: '',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const [tableData, setTableData] = useState<
    MachineTableData[] | UserTableData[]
  >([]);
  const [userId, setUserId] = useState<string>('');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const { ongoingExecutions } = useAppSelector(state => {
    return state.hostExecutionDrawer;
  });
  const [fetchMachineDetails] = useLazyGetExecutionDetailsQuery();
  const { data: getUserData } = useGetUserDetailsQuery(
    { userId: userId },
    { skip: !userId },
  );

  const License_TYPE: LicenseType = getStoreValue('test-optimize-user') || {};
  const { name, licenseName, privilege } = License_TYPE.responseObject || {};
  const [showTerminateWarning, setShowTerminateWarning] =
    useState<boolean>(false);

  const [selectedExecution, setSelectedExecution] = useState<{
    executionId: string;
    initiatedBy: string;
    executionType: string;
    type: string;
  } | null>(null);
  const [triggerTerminateExecution] = useTerminateExecutionMutation();
  const handleTerminateExecution = async (data: {
    executionId: string;
    executionType: string;
    type: string;
  }) => {
    if (!data.executionId) return false;

    try {
      const response = await triggerTerminateExecution({
        executionId: data.executionId,
        executionType: data.executionType,
        treeType:
          data?.type === 'INDIVIDUAL' ? 'testDev' : data?.type?.toLowerCase(),
      }).unwrap();

      if (response.responseCode === 200) {
        toast.success(response.message);
        onGoingFetchData();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error terminating execution:', error);
      return false;
    }
  };
  const handleTerminateClick = (row: {
    executionId: string;
    initiatedBy: string;
    executionType: string;
    type: string;
  }) => {
    // 1. Execution owner - directly terminate
    if (name === row?.initiatedBy) {
      handleTerminateExecution(row);
      return;
    }

    // 2. Super Admin terminating another user's execution -> warning
    if (privilege === 'Super Admin') {
      setSelectedExecution(row);
      setShowTerminateWarning(true);
    }
  };
  const handleWarningModalContinue = async () => {
    if (!selectedExecution) return;

    const isTerminated = await handleTerminateExecution(selectedExecution);

    if (isTerminated) {
      setShowTerminateWarning(false);
      setSelectedExecution(null);
    }
  };
  const handleWarningModalCancel = () => {
    setShowTerminateWarning(false);
    setSelectedExecution(null);
  };
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!checkEmpty(rowData)) {
      dispatch(loadOngoingExecutions(rowData));
    }
  }, [rowData]);

  useEffect(() => {
    if (userId && getUserData?.responseCode === 200) {
      const userDetails = getUserData?.responseObject;
      const userPrivilege = License_TYPE?.responseObject?.privilege || 'User';
      setData({
        name: userDetails?.name,
        email: userDetails?.emailId,
        privilege: userPrivilege,
        status: userDetails?.activationStatus,
        createdBy: userDetails?.createdByUname,
        createdOn: userDetails?.createdOn,
        modifiedBy: userDetails?.modifiedByUname,
        modifiedOn: userDetails?.modifiedOn,
      });
    }
  }, [userId, getUserData]);

  const handleShowDetails = async (type: string, value: string) => {
    setType(type);
    const response = await fetchMachineDetails();
    if (
      response?.data?.responseCode === 200 &&
      response?.data?.responseCode !== undefined
    ) {
      if (type === 'Machine') {
        const machine = response?.data?.responseObject.filter(data => {
          if (data?.preExecution?.selectedSystem) {
            const machine = data?.preExecution?.selectedSystem;
            const SystemData = machine?.some(system =>
              value?.includes(
                system?.machineInstances?.[0]?.machineInfo?.hostName,
              ),
            );
            if (SystemData) {
              return data;
            }
          }
        });

        if (!checkEmpty(machine)) {
          const foundedMachine =
            machine?.[0]?.preExecution?.selectedSystem.filter(
              (system: DynamicObj) =>
                value?.includes(
                  system?.machineInstances?.[0]?.machineInfo?.hostName,
                ),
            );

          const machineDetails = {
            os:
              foundedMachine?.[0]?.machineInstances?.[0]?.machineInfo
                ?.hostName || '---',
            browser:
              foundedMachine?.[0]?.machineInstances?.[0]?.machineInfo?.osName ||
              '---',
            device: '---',
            owner: machine?.[0]?.createdByUname || '---',
          };
          setData(machineDetails);
        }

        const machineTableData = response?.data?.responseObject
          .filter(data => {
            if (data?.preExecution?.selectedSystem) {
              const machine = data?.preExecution?.selectedSystem;
              const SystemData = machine?.some((system: any) => {
                if (system?.machineInstances?.[0]?.machineInfo?.hostName) {
                  return value?.includes(
                    system?.machineInstances?.[0]?.machineInfo?.hostName,
                  );
                }
              });

              if (SystemData) {
                return data;
              }
            }
          })
          .map((data: ExecutionResponseObjType) => ({
            executionId: data?.executionName || data?._id,
            executionName: data?.name ? data.name : 'N/A',
            project: data?.projectName,
            initiatedBy: data?.createdByUname,
            status: data?.executionStatus,
          }));

        setTableData(machineTableData);
      } else if (type === 'User') {
        const getUserId = response?.data?.responseObject
          .filter(
            (data: { createdByUname: string }) =>
              data?.createdByUname === value,
          )
          .map((data: { createdBy: string }) => data?.createdBy);
        setUserId(getUserId[0]);
        const userTableData = response?.data?.responseObject
          .filter(
            (data: { createdByUname: string }) =>
              data?.createdByUname === value,
          )
          .map(data => ({
            executionId: data?._id,
            executionName: data?.name ? data.name : 'N/A',
            project: data?.projectName,
            machineName:
              data?.preExecution?.selectedSystem
                ?.map(
                  system =>
                    system?.machineInstances
                      ?.map(
                        (instance: { machineInfo: { hostName: string } }) =>
                          instance?.machineInfo?.hostName,
                      )
                      .join(', ') || 'MachineName Not Available',
                )
                .join(', ') || 'MachineName Not Available',
            status: data?.executionStatus,
          }));
        setTableData(userTableData);
      }
    }
    setShowDetails(true);
  };
  const handleCloseDetails = () => {
    setShowDetails(false);
  };
  const handleCloseSearch = () => {
    setIsExpand(false);
    setSearchTerm('');
  };

  const columnData = [
    {
      header: 'Execution Id',
      accessor: 'executionId',
      width: 118,
      cell: (e: {
        value: string;
        row: {
          executionId: string;
          initiatedBy: string;
          executionType: string;
          type: string;
        };
      }) => {
        return (
          <div className={styles.execution_icon}>
            <AutoTruncateText>
              <HighlightText highlight={searchTerm} text={e?.value} />
            </AutoTruncateText>
          </div>
        );
      },
      actions: (row: {
        executionId: string;
        initiatedBy: string;
        executionType: string;
        type: string;
      }) => {
        const canTerminate =
          terminated.includes(`${row?.executionId}`) &&
          (name === row?.initiatedBy || privilege === 'Super Admin');
        return (
          canTerminate && (
            <div className={styles.execution_icon_hover}>
              <Icon
                name="terminated"
                height={16}
                width={16}
                onClick={() => handleTerminateClick(row)}
              />
            </div>
          )
        );
      },
    },
    {
      header: 'Execution Name',
      accessor: 'executionName',
      width: 176,
      cell: (e: { value: string }) => (
        <div className={styles.cell_container}>
          <div className={styles.cell_container_text}>
            <AutoTruncateText>
              <HighlightText highlight={searchTerm} text={e?.value} />
            </AutoTruncateText>
          </div>
          <span className={styles.cell_container_chip}>
            {scriptType.includes(`${e?.value}`) && (
              <Chip label="S" variant="primary" fullText="Suite" />
            )}
          </span>
        </div>
      ),
    },
    {
      header: 'Project',
      accessor: 'project',
      width: 176,
      cell: (e: { value: string }) => (
        <AutoTruncateText>
          <HighlightText highlight={searchTerm} text={e?.value} />
        </AutoTruncateText>
      ),
    },
    {
      header: 'Machine Name',
      accessor: 'machineName',
      width: 170,
      cell: (e: { value: string }) => {
        const macName = e?.value?.split(',');
        return (
          <div className={styles.machine_name_display}>
            {macName?.map((name, index) => (
              <Typography
                key={index}
                as="div"
                color="var(--brand-color)"
                onClick={() => handleShowDetails('Machine', name)}
                className={styles.machine_user_text}
              >
                <AutoTruncateText>
                  <HighlightText highlight={searchTerm} text={String(name)} />
                  {macName?.length > 1 &&
                  index === 0 &&
                  macName?.length !== index
                    ? ','
                    : ''}
                </AutoTruncateText>
              </Typography>
            ))}
          </div>
        );
      },
    },
    {
      header: 'Initiated By',
      accessor: 'initiatedBy',
      width: 120,
      cell: (e: { value: string }) => (
        <Typography
          color="var(--brand-color)"
          onClick={() => handleShowDetails('User', e?.value)}
          className={styles.machine_user_text}
        >
          <AutoTruncateText>
            <HighlightText highlight={searchTerm} text={String(e?.value)} />
          </AutoTruncateText>
        </Typography>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 120,
      cell: (e: { value: string }) => (
        <Typography
          color="var(--table-header-text-color)"
          className={styles.status_text}
        >
          <AutoTruncateText>
            <HighlightText highlight={searchTerm} text={e?.value} />
          </AutoTruncateText>
        </Typography>
      ),
    },
  ];

  useEffect(() => {
    if (searchTerm) {
      const searchData = searchTerm.toLowerCase();
      const filteredLabelsList = rowData.filter(
        data =>
          data.executionId.toLowerCase().includes(searchData) ||
          data?.executionName?.toLowerCase()?.includes(searchData) ||
          data?.project?.toLowerCase()?.includes(searchData) ||
          data?.initiatedBy?.toLowerCase()?.includes(searchData) ||
          data?.status?.toLowerCase()?.includes(searchData) ||
          data?.machineName?.toLowerCase()?.includes(searchData),
      );
      dispatch(loadOngoingExecutions([...filteredLabelsList]));
    } else {
      dispatch(loadOngoingExecutions([...rowData]));
    }
  }, [searchTerm, rowData]);

  const unsubscribeWebSocketAndCloseTheDrawer = () => {
    onClose();
  };

  const NoDataContentForExecution = () => {
    return (
      <div className={styles.execution_details_no_data_message}>
        <div className={styles.no_data_message_headers}>
          <div className={styles.no_data_message_header}>
            <Typography as={'div'} fontSize={18} fontWeight="semi-bold">
              No Ongoing Executions
            </Typography>
          </div>
          <Typography
            as={'div'}
            fontSize={18}
            fontWeight="semi-bold"
            textAlign="center"
          >
            There are no executions running right now.
          </Typography>
        </div>
        <div className={styles.no_data_message_text}>
          <Typography as={'div'} fontSize={14} textAlign="center">
            Trigger an execution to view live progress and details here
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <>
      <Drawer
        isOpen
        customHeader={
          <div className={styles.drawer_header}>
            <Typography
              as="div"
              fontWeight="semi-bold"
              color="var(--drawer-title-color)"
            >
              {`Ongoing Executions - ${licenseName}`}
            </Typography>
            <div className={styles.header_right}>
              <Search
                placeholder="Search"
                isExpand={isExpand}
                value={searchTerm}
                onSearch={setSearchTerm}
                onExpand={(expand: boolean) => setIsExpand(expand)}
                onClose={handleCloseSearch}
                disabled={checkEmpty(rowData)}
              />
              <Icon
                name="refresh_icon"
                onClick={onGoingFetchData}
                className={styles.sync_btn}
                disabled={checkEmpty(rowData)}
                data-tooltip="Sync"
              />
              <Icon
                name="drawer_icon_close"
                onClick={unsubscribeWebSocketAndCloseTheDrawer}
                height={16}
                width={16}
                color="var(--tabs-label-active-color)"
                cursorType="pointer"
                data-tooltip="Close"
              />
            </div>
          </div>
        }
        onClose={unsubscribeWebSocketAndCloseTheDrawer}
        isBackButtonVisible={false}
        size="large"
        top="56px"
        overlay
        isClickOutside={!showTerminateWarning}
        zIndex={9999}
        isFooterRequired={false}
      >
        <div className={styles.execution_details}>
          <Table
            data={ongoingExecutions}
            columns={columnData}
            noDataContent={
              searchTerm.trim() && checkEmpty(ongoingExecutions) ? (
                <NoDataContent
                  iconName="no_license_found"
                  text="No Result found"
                />
              ) : (
                NoDataContentForExecution()
              )
            }
            headerType="secondary"
            tableDataTextColor="var(--table-header-text-color)"
            height="78dvh"
            tableHeaderZindex={1200}
          />
        </div>
      </Drawer>
      {showDetails && (
        <MachineOrUserDetailsDrawer
          onClose={handleCloseDetails}
          data={details}
          type={type}
          tableData={tableData}
        />
      )}
      {showTerminateWarning && selectedExecution && (
        <PopUpModal
          iconName="license_warning"
          titleMessage="Warning!"
          subTitleMessage=""
          modalMessage={
            <>
              Are you sure you want to terminate{' '}
              <Typography fontSize={16} as="span" fontWeight="semi-bold">
                <AutoTruncateText lines={6}>
                  {selectedExecution.executionId}
                </AutoTruncateText>
              </Typography>{' '}
              execution triggered by{' '}
              <Typography fontSize={16} as="span" fontWeight="semi-bold">
                {selectedExecution.initiatedBy}
              </Typography>
              ?
            </>
          }
          firstButtonLabel="Cancel"
          secondButtonLabel="Continue"
          buttonVariant="warning"
          border="1px solid var(--warning-modal-border-color)"
          colorForTitleMessage="var(--status-button-bg-warning)"
          popupWidth="420"
          footerContent={
            <Typography fontSize={16}>How do you want to proceed?</Typography>
          }
          isOpen={showTerminateWarning}
          onClose={handleWarningModalCancel}
          onContinue={handleWarningModalContinue}
          zIndex={10001}
          popupHeight="auto"
        />
      )}
    </>
  );
};
export default ExecutionDetails;
